import { apiClient, ApiResponse } from './apiClient';

// Property Type interfaces matching your API structure
export interface PropertyType {
  id: number;
  name_en: string;
  name_ar: string;
  description_en: string;
  description_ar: string;
  icon: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePropertyTypeRequest {
  name_en: string;
  name_ar: string;
  description_en: string;
  description_ar: string;
  icon: string;
}

export interface UpdatePropertyTypeRequest {
  name_en?: string;
  name_ar?: string;
  description_en?: string;
  description_ar?: string;
  icon?: string;
  is_active?: boolean;
}

export interface PropertyTypeListResponse {
  propertyTypes: PropertyType[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PropertyTypeSearchFilters {
  searchTerm?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}

// Property Types Service Class
export class PropertyTypesService {
  private baseUrl = '/properties/types';

  // Get all property types with search and filters
  async getPropertyTypes(filters: PropertyTypeSearchFilters = {}): Promise<ApiResponse<PropertyTypeListResponse>> {
    const params = new URLSearchParams();
    
    if (filters.searchTerm) params.append('search', filters.searchTerm);
    if (filters.isActive !== undefined) params.append('is_active', filters.isActive.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    try {
      console.log('🔧 PropertyTypesService.getPropertyTypes called:', {
        url,
        filters
      });
      
      const response = await apiClient.get<PropertyType[]>(url);
      
      console.log('🔧 PropertyTypesService.getPropertyTypes raw response:', response);
      
      if (response.success && response.data) {
        // The API returns data directly as an array after apiClient transformation
        const propertyTypes = Array.isArray(response.data) ? response.data : [];
        
        console.log('🔧 PropertyTypesService received data type:', typeof response.data);
        console.log('🔧 PropertyTypesService is array?:', Array.isArray(response.data));
        console.log('🔧 PropertyTypesService data content:', response.data);
        
        console.log('🔧 PropertyTypesService.getPropertyTypes propertyTypes array:', propertyTypes);
        
        // Apply client-side filtering if API doesn't support it
        let filteredTypes = propertyTypes;
        
        console.log('🔧 Before filtering:', {
          originalTypes: propertyTypes,
          filters,
          searchTerm: filters.searchTerm,
          isActive: filters.isActive
        });
        
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const beforeFilter = filteredTypes.length;
          filteredTypes = filteredTypes.filter(type => 
            type.name_en.toLowerCase().includes(searchLower) ||
            type.name_ar.includes(filters.searchTerm!) ||
            type.description_en.toLowerCase().includes(searchLower) ||
            type.description_ar.includes(filters.searchTerm!)
          );
          console.log('🔧 After search filter:', {
            beforeCount: beforeFilter,
            afterCount: filteredTypes.length,
            searchTerm: filters.searchTerm
          });
        }
        
        if (filters.isActive !== undefined) {
          const beforeFilter = filteredTypes.length;
          filteredTypes = filteredTypes.filter(type => type.is_active === filters.isActive);
          console.log('🔧 After active filter:', {
            beforeCount: beforeFilter,
            afterCount: filteredTypes.length,
            isActive: filters.isActive
          });
        }
        
        // Simple pagination
        const page = filters.page || 1;
        const pageSize = filters.pageSize || 25;
        const total = filteredTypes.length;
        const totalPages = Math.ceil(total / pageSize);
        const startIndex = (page - 1) * pageSize;
        const paginatedTypes = filteredTypes.slice(startIndex, startIndex + pageSize);
        
        const listResponse: PropertyTypeListResponse = {
          propertyTypes: paginatedTypes,
          total,
          page,
          pageSize,
          totalPages
        };
        
        console.log('🔧 PropertyTypesService.getPropertyTypes final result:', {
          originalCount: propertyTypes.length,
          filteredCount: filteredTypes.length,
          paginatedCount: paginatedTypes.length,
          listResponse
        });
        
        return {
          success: true,
          data: listResponse,
          message: 'Property types fetched successfully'
        };
      }
      
      return {
        success: false,
        data: { propertyTypes: [], total: 0, page: 1, pageSize: 25, totalPages: 0 },
        message: 'Failed to fetch property types'
      };
    } catch (error) {
      console.error('Error fetching property types:', error);
      return {
        success: false,
        data: { propertyTypes: [], total: 0, page: 1, pageSize: 25, totalPages: 0 },
        message: 'Error fetching property types'
      };
    }
  }

  // Get property type by ID
  async getPropertyTypeById(id: number): Promise<ApiResponse<PropertyType>> {
    try {
      const response = await apiClient.get<PropertyType>(`${this.baseUrl}/${id}`);
      return response;
    } catch (error) {
      console.error('Error fetching property type by ID:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error fetching property type'
      };
    }
  }

  // Create new property type (matches your POST /properties/types)
  async createPropertyType(data: CreatePropertyTypeRequest): Promise<ApiResponse<PropertyType>> {
    try {
      const response = await apiClient.post<PropertyType>(this.baseUrl, data);
      return response;
    } catch (error) {
      console.error('Error creating property type:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error creating property type'
      };
    }
  }

  // Update property type
  async updatePropertyType(id: number, data: UpdatePropertyTypeRequest): Promise<ApiResponse<PropertyType>> {
    try {
      console.log('🔧 PropertyTypesService.updatePropertyType called:', {
        id,
        data,
        url: `${this.baseUrl}/${id}`
      });
      
      const response = await apiClient.patch<PropertyType>(`${this.baseUrl}/${id}`, data);
      
      console.log('🔧 PropertyTypesService.updatePropertyType response:', response);
      
      return response;
    } catch (error) {
      console.error('🔧 PropertyTypesService.updatePropertyType error:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error updating property type'
      };
    }
  }

  // Delete property type
  async deletePropertyType(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<void>(`${this.baseUrl}/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting property type:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error deleting property type'
      };
    }
  }

  // Toggle property type active status
  async togglePropertyTypeStatus(id: number, isActive: boolean): Promise<ApiResponse<PropertyType>> {
    try {
      const response = await apiClient.patch<PropertyType>(`${this.baseUrl}/${id}`, { is_active: isActive });
      return response;
    } catch (error) {
      console.error('Error toggling property type status:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error updating property type status'
      };
    }
  }

  // Bulk operations
  async bulkUpdatePropertyTypes(ids: number[], action: string, data?: any): Promise<ApiResponse<void>> {
    // For now, use individual API calls for each property type
    const promises = ids.map(id => {
      switch (action) {
        case 'activate':
          return this.togglePropertyTypeStatus(id, true);
        case 'deactivate':
          return this.togglePropertyTypeStatus(id, false);
        case 'delete':
          return this.deletePropertyType(id);
        default:
          return Promise.resolve({ success: true, data: null });
      }
    });

    await Promise.all(promises);
    return { success: true, data: undefined, message: 'Bulk operation completed' };
  }
}

// Export singleton instance
export const propertyTypesService = new PropertyTypesService();
export default propertyTypesService;
